/**
 * Desenho da vitrine de links (estilo Linktree) — UM lugar só.
 *
 * A página pública `/l/<apelido>` e a prévia da aba Links chamam esta mesma função, então o
 * que a equipe vê ao editar é o que o paciente vê ao abrir. DOM puro, sem React: a página
 * pública precisa nascer leve. Todo texto entra por `textContent` (nada de HTML vindo do
 * documento) e todo endereço passa por `destinoSeguro`/`imagemSegura` de novo aqui.
 */
import {
  resolverTema, destinoSeguro, imagemSegura, blocosDaPagina, blocoVisivelAgora,
  enderecoIncorporado, alturaIncorporado, detectarRede, REDES, FONTES, validarContato, destinoComMensagem, ehWhatsapp,
} from '../links/linksModelo.js';
import { svgDaRede } from './icones.js';

const el = (tag, cls, texto) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (texto != null) e.textContent = texto;
  return e;
};

/** Carrega a fonte escolhida (uma vez por família). */
export function carregarFonte(id) {
  const f = FONTES[id];
  if (!f || typeof document === 'undefined') return;
  const href = `https://fonts.googleapis.com/css2?family=${f.google}&display=swap`;
  if (document.querySelector(`link[data-lkv-fonte="${id}"]`)) return;
  const l = document.createElement('link');
  l.rel = 'stylesheet';
  l.href = href;
  l.dataset.lkvFonte = id;
  document.head.append(l);
}

/**
 * @param {HTMLElement} alvo
 * @param {object} pagina  `doc.pagina`
 * @param {{
 *   aoClicar?: (bloco: object) => void,
 *   aoClicarRede?: (rede: object) => void,
 *   aoEnviarContato?: (bloco: object, dados: object) => Promise<void>,
 *   previa?: boolean,
 *   estatico?: boolean,
 *   agora?: number,
 * }} [opcoes]
 */
/**
 * WhatsApp abre em outra aba (no celular, direto no aplicativo): a página fica, e o evento de
 * conversão do Pixel termina de sair em vez de ser cortado pela troca de página.
 */
function abreFora(a) {
  if (ehWhatsapp(a.href)) a.target = '_blank';
}

export function desenharVitrine(alvo, pagina, opcoes = {}) {
  const tema = resolverTema(pagina);
  const agora = opcoes.agora || Date.now();
  carregarFonte(tema.fonte);
  alvo.replaceChildren();
  alvo.classList.add('lkv');
  const st = alvo.style;
  st.setProperty('--lkv-fundo', tema.fundoCss);
  st.setProperty('--lkv-fundo-base', tema.corBase);
  st.setProperty('--lkv-texto', tema.texto);
  st.setProperty('--lkv-botao', tema.botao);
  st.setProperty('--lkv-botao-texto', tema.botaoTextoFinal);
  st.setProperty('--lkv-botao-borda', tema.botaoBorda);
  st.setProperty('--lkv-raio', tema.raio);
  st.setProperty('--lkv-fonte', tema.fonteCss);
  // Ajustes finos (aba Aparência): cada um vira uma variável que a folha usa.
  const vars = {
    '--lkv-botao-borda-largura': tema.botaoBordaLargura,
    '--lkv-botao-sombra': tema.botaoSombraCss,
    '--lkv-botao-altura': `${tema.botaoAltura}px`,
    '--lkv-botao-espaco': `${tema.botaoEspaco}px`,
    '--lkv-botao-fonte': `${tema.botaoFonteTamanho}px`,
    '--lkv-botao-alinha': tema.botaoAlinhamento === 'esquerda' ? 'flex-start' : 'center',
    '--lkv-botao-texto-alinha': tema.botaoAlinhamento === 'esquerda' ? 'left' : 'center',
    '--lkv-nome-fonte': tema.nomeFonteCss,
    '--lkv-nome-tamanho': `${tema.nomeTamanho}px`,
    '--lkv-nome-estilo': tema.nomeItalico ? 'italic' : 'normal',
    '--lkv-bio-tamanho': `${tema.bioTamanho}px`,
    '--lkv-bio-caixa': tema.bioCaixaAlta ? 'uppercase' : 'none',
    '--lkv-bio-tracking': tema.bioCaixaAlta ? '0.12em' : '0',
    '--lkv-capa-altura': String(tema.capaAltura),
    '--lkv-capa-foco': `${tema.capaFoco}%`,
    '--lkv-capa-escurecer': String(tema.capaEscurecer / 100),
    '--lkv-cartao-fundo': tema.cartaoFundoFinal,
    '--lkv-cartao-texto': tema.cartaoTextoFinal,
    '--lkv-icone-tamanho': `${tema.iconeTamanho}px`,
    '--lkv-icone-cor': tema.iconeCorFinal,
  };
  for (const [k, v] of Object.entries(vars)) st.setProperty(k, v);
  if (tema.nomeCorFinal) st.setProperty('--lkv-nome-cor', tema.nomeCorFinal); else st.removeProperty('--lkv-nome-cor');
  if (tema.bioCorFinal) st.setProperty('--lkv-bio-cor', tema.bioCorFinal); else st.removeProperty('--lkv-bio-cor');
  if (tema.nomeFonte) carregarFonte(tema.nomeFonte);
  alvo.dataset.estilo = tema.botaoEstilo;
  alvo.dataset.fundo = tema.fundoTipo;
  alvo.dataset.sociais = tema.sociais;

  // Miniatura dentro de um botão (seletor de temas): nada de link nem título de verdade.
  const est = !!opcoes.estatico;
  const tagLink = est ? 'span' : 'a';
  const col = el('div', 'lkv-col');
  const nome = String(pagina?.nome || '').trim();
  const foto = imagemSegura(pagina?.foto);
  // «CRM-SP», «RQE-123»: o hífen entre letras/números não quebra a linha (fica feio em caixa alta).
  // O «·» que separa partes da frase fica preso à palavra de antes (não abre a linha seguinte).
  const bio = String(pagina?.bio || '').trim()
    .replace(/([\p{L}\d])-(?=[\p{L}\d])/gu, '$1\u2011')
    .replace(/ ([·|•]) /g, '\u00a0$1 ');

  const sociais = (pagina?.sociais || []).filter((s) => destinoSeguro(s.url));
  const linhaSociais = () => {
    const ul = el('ul', 'lkv-sociais');
    for (const s of sociais) {
      const li = el('li');
      const a = el(tagLink, 'lkv-social');
      if (!est) { a.href = destinoSeguro(destinoComMensagem(destinoSeguro(s.url), s.mensagem)); a.rel = 'noopener'; abreFora(a); }
      const rede = REDES[s.rede] ? s.rede : detectarRede(s.url);
      a.setAttribute('aria-label', REDES[rede]?.nome || 'Link');
      a.append(svgDaRede(rede));
      if (opcoes.previa) {
        a.addEventListener('click', (e) => e.preventDefault());
        a.tabIndex = -1;
      } else if (!est) {
        a.addEventListener('click', () => opcoes.aoClicarRede?.(s));
      }
      li.append(a);
      ul.append(li);
    }
    return ul;
  };
  const sociaisNoTopo = sociais.length && pagina?.sociaisPosicao !== 'rodape';

  // «Foto em destaque»: a foto ocupa a largura do topo e o nome, a frase e os ícones ficam
  // em branco sobre o escurecido da parte de baixo dela (como os modelos com foto de capa).
  if (tema.perfil === 'destaque' && foto) {
    alvo.dataset.perfil = 'destaque';
    const hero = el('header', 'lkv-hero');
    const img = el('img', 'lkv-hero-foto');
    img.src = foto;
    img.alt = nome ? `Foto de ${nome}` : '';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', () => { img.remove(); hero.classList.add('lkv-hero--sem-foto'); });
    const texto = el('div', 'lkv-hero-texto');
    if (nome) texto.append(el(est ? 'div' : 'h1', 'lkv-nome', nome));
    if (bio) texto.append(el('p', 'lkv-bio', bio));
    if (sociaisNoTopo) texto.append(linhaSociais());
    hero.append(img, texto);
    col.append(hero);
  } else if (tema.perfil === 'cartao' && foto) {
    // «Foto com cartão»: capa de ponta a ponta e um cartão claro com o nome sobreposto à base
    // da foto, como os modelos editoriais. Os ícones vêm logo abaixo, já no fundo da página.
    alvo.dataset.perfil = 'cartao';
    const hero = el('header', 'lkv-capa');
    const img = el('img', 'lkv-hero-foto');
    img.src = foto;
    img.alt = nome ? `Foto de ${nome}` : '';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', () => img.remove());
    hero.append(img);
    col.append(hero);
    if (nome || bio) {
      const cartao = el('div', 'lkv-cartao');
      if (nome) cartao.append(el(est ? 'div' : 'h1', 'lkv-nome', nome));
      if (bio) cartao.append(el('p', 'lkv-bio', bio));
      col.append(cartao);
    }
    if (sociaisNoTopo) col.append(linhaSociais());
  } else {
    delete alvo.dataset.perfil;
    if (foto) {
      const img = el('img', 'lkv-foto');
      img.src = foto;
      img.alt = nome ? `Foto de ${nome}` : '';
      img.referrerPolicy = 'no-referrer';
      img.addEventListener('error', () => img.replaceWith(inicial(nome)));
      col.append(img);
    } else if (nome) {
      col.append(inicial(nome));
    }
    if (nome) col.append(el(est ? 'div' : 'h1', 'lkv-nome', nome));
    if (bio) col.append(el('p', 'lkv-bio', bio));
    if (sociaisNoTopo) col.append(linhaSociais());
  }

  const lista = el('div', 'lkv-blocos');
  for (const b of blocosDaPagina(pagina)) {
    if (!blocoVisivelAgora(b, agora)) continue;
    const no = desenharBloco(b, { ...opcoes, tagLink }, alvo);
    if (no) lista.append(no);
  }
  // Na prévia, página ainda sem botão válido mostra onde eles vão aparecer (nunca na pública).
  if (opcoes.previa && !lista.children.length) {
    for (const t of ['Seu primeiro botão', 'Outro botão']) {
      const g = el('span', 'lkv-botao lkv-fantasma');
      g.append(el('span', 'lkv-botao-rotulo', t));
      lista.append(g);
    }
  }
  col.append(lista);
  if (sociais.length && pagina?.sociaisPosicao === 'rodape') col.append(linhaSociais());

  // Assinatura da Messa no rodapé, como a do Linktree: discreta, na cor do texto do tema.
  const marca = el(tagLink, 'lkv-marca');
  if (!est) {
    marca.href = 'https://messamkt.com/?utm_source=pagina-de-links&utm_medium=rodape';
    marca.target = '_blank';
    marca.rel = 'noopener';
  }
  marca.setAttribute('aria-label', 'Feito pela Messa Marketing Médico');
  marca.append(el('span', 'lkv-marca-logo'));
  if (opcoes.previa) {
    marca.addEventListener('click', (e) => e.preventDefault());
    marca.tabIndex = -1;
  }
  col.append(marca);
  alvo.append(col);

  if (pagina?.sensivel && !opcoes.previa) {
    alvo.append(aviso18(alvo, () => {}));
  }
}

function inicial(nome) {
  const d = el('div', 'lkv-foto lkv-foto--inicial', (String(nome || '').trim()[0] || '·').toUpperCase());
  d.setAttribute('aria-hidden', 'true');
  return d;
}

function desenharBloco(b, opcoes, raiz) {
  const tipo = b.tipo || 'link';
  if (tipo === 'titulo') {
    const t = String(b.rotulo || '').trim();
    return t ? el(opcoes.estatico ? 'div' : 'h2', 'lkv-titulo', t) : null;
  }
  if (tipo === 'texto') {
    const t = String(b.rotulo || '').trim();
    return t ? el('p', 'lkv-texto', t) : null;
  }
  if (tipo === 'video' || tipo === 'musica') {
    const src = enderecoIncorporado(b.url);
    if (!src) return null;
    const box = el('div', tipo === 'video' ? 'lkv-midia lkv-midia--video' : 'lkv-midia');
    const h = alturaIncorporado(src);
    if (h) box.style.height = `${h}px`;
    const f = document.createElement('iframe');
    f.src = src;
    f.title = String(b.rotulo || '').trim() || (tipo === 'video' ? 'Vídeo' : 'Player');
    f.loading = 'lazy';
    f.allow = 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture';
    f.referrerPolicy = 'strict-origin-when-cross-origin';
    f.setAttribute('allowfullscreen', '');
    if (b.rotulo) box.append(el('p', 'lkv-midia-rotulo', b.rotulo));
    box.append(f);
    return box;
  }
  if (tipo === 'contato') return formularioContato(b, opcoes);

  // link
  const url = destinoSeguro(destinoComMensagem(destinoSeguro(b.url), b.mensagem));
  if (!url || !String(b.rotulo || '').trim()) return null;
  const a = el(opcoes.tagLink || 'a', `lkv-botao${b.destaque && b.destaque !== 'nenhum' ? ` lkv-destaque--${b.destaque}` : ''}`);
  if (!opcoes.estatico) { a.href = url; a.rel = 'noopener'; abreFora(a); }
  const mini = imagemSegura(b.miniatura);
  if (mini) {
    const img = el('img', 'lkv-mini');
    img.src = mini;
    img.alt = '';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', () => img.remove());
    a.append(img);
    a.classList.add('lkv-botao--mini');
  }
  a.append(el('span', 'lkv-botao-rotulo', b.rotulo));
  if (opcoes.previa) {
    a.addEventListener('click', (e) => e.preventDefault());
    a.tabIndex = -1;
  } else {
    a.addEventListener('click', (e) => {
      if (b.sensivel) {
        e.preventDefault();
        raiz.append(aviso18(raiz, () => {
          opcoes.aoClicar?.(b);
          window.location.href = url;
        }, true));
        return;
      }
      opcoes.aoClicar?.(b);
    });
  }
  return a;
}

/** Aviso de conteúdo sensível (+18). `doLink` = vem de um botão, não da página inteira. */
function aviso18(raiz, aoSeguir, doLink = false) {
  const fundo = el('div', 'lkv-aviso');
  fundo.setAttribute('role', 'dialog');
  fundo.setAttribute('aria-modal', 'true');
  const caixa = el('div', 'lkv-aviso-caixa');
  caixa.append(
    el('p', 'lkv-aviso-titulo', 'Conteúdo sensível'),
    el('p', 'lkv-aviso-texto', doLink
      ? 'Este link pode mostrar imagens ou temas que nem todo mundo quer ver. Continuar?'
      : 'Esta página pode mostrar imagens ou temas que nem todo mundo quer ver. Ao continuar, você confirma ter 18 anos ou mais.'),
  );
  const ok = el('button', 'lkv-aviso-ok', doLink ? 'Continuar' : 'Tenho 18 anos ou mais');
  ok.type = 'button';
  ok.addEventListener('click', () => { fundo.remove(); aoSeguir(); });
  caixa.append(ok);
  if (doLink) {
    const nao = el('button', 'lkv-aviso-nao', 'Voltar');
    nao.type = 'button';
    nao.addEventListener('click', () => fundo.remove());
    caixa.append(nao);
  }
  fundo.append(caixa);
  setTimeout(() => ok.focus(), 0);
  return fundo;
}

const ROTULOS_CAMPO = { nome: 'Seu nome', email: 'Seu e-mail', telefone: 'Seu WhatsApp com DDD' };
const TIPOS_CAMPO = { nome: 'text', email: 'email', telefone: 'tel' };
const AUTOCOMPLETE = { nome: 'name', email: 'email', telefone: 'tel' };

function formularioContato(b, opcoes) {
  const form = el('form', 'lkv-form');
  form.noValidate = true;
  if (b.rotulo) form.append(el('p', 'lkv-form-titulo', b.rotulo));
  const campos = Array.isArray(b.campos) && b.campos.length ? b.campos : ['nome', 'telefone'];
  for (const c of campos) {
    const inp = el('input', 'lkv-campo');
    inp.name = c;
    inp.type = TIPOS_CAMPO[c] || 'text';
    inp.placeholder = ROTULOS_CAMPO[c] || c;
    inp.setAttribute('aria-label', ROTULOS_CAMPO[c] || c);
    inp.autocomplete = AUTOCOMPLETE[c] || 'on';
    if (opcoes.previa) inp.tabIndex = -1;
    form.append(inp);
  }
  const msg = el('p', 'lkv-form-msg');
  msg.setAttribute('role', 'status');
  const enviar = el('button', 'lkv-botao lkv-form-enviar', 'Enviar');
  enviar.type = 'submit';
  if (opcoes.previa) enviar.tabIndex = -1;
  form.append(enviar, msg);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (opcoes.previa || !opcoes.aoEnviarContato) return;
    const entrada = Object.fromEntries(new FormData(form).entries());
    const { dados, erro } = validarContato(entrada, campos);
    if (erro) { msg.textContent = erro; return; }
    enviar.disabled = true;
    msg.textContent = 'Enviando…';
    try {
      await opcoes.aoEnviarContato(b, dados);
      form.replaceChildren(el('p', 'lkv-form-ok', String(b.agradecimento || '').trim() || 'Recebemos seu contato. Obrigado!'));
    } catch {
      enviar.disabled = false;
      msg.textContent = 'Não foi possível enviar agora. Tente de novo.';
    }
  });
  return form;
}
